<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Adenda masiva</h1>
    </div>

    <wz-card style="max-width: 1000px" :body-padding="false">
      <template #header>
        <div class="wz-card-title">
          <span v-if="currentStep === 1">Paso 1: Empresa y selección de contratos</span>
          <span v-if="currentStep === 2">Paso 2: Campos a modificar, fecha, motivo y PDF</span>
          <span v-if="currentStep === 3">Paso 3: Confirmación</span>
        </div>
      </template>

      <section class="form-section mb-0" style="padding: 16px 20px;">

        <!-- ───────────────── PASO 1 ───────────────── -->
        <div v-if="currentStep === 1">
          <div class="mb-3" style="max-width: 420px;">
            <label>Empresa <span class="text-danger">*</span></label>
            <wz-select :items="companyOptions" v-model="companyId" defaultOption="Seleccionar empresa" @change="onCompanyChange" />
            <small class="text-muted d-block mt-1">El lote se aplica a contratos de una sola empresa.</small>
          </div>

          <template v-if="companyId">
            <div class="mb-3" style="max-width: 420px;">
              <input type="text" v-model="search" placeholder="Buscar por nombre o DNI" class="wz-form-control" />
            </div>

            <div class="flex items-center mb-2">
              <span class="mr-3 text-muted">{{ selectedIds.length }} de {{ filteredContracts.length }} contrato(s) seleccionado(s)</span>
            </div>

            <div class="overflow-x-auto">
              <table class="wz-table w-full">
                <thead>
                  <tr>
                    <th style="width: 40px">
                      <input type="checkbox" :checked="allVisibleSelected" @change="toggleSelectAll" :disabled="!filteredContracts.length" />
                    </th>
                    <th>Colaborador</th>
                    <th>DNI</th>
                    <th>Cargo</th>
                    <th>Área</th>
                    <th class="text-right">Sueldo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loadingContracts">
                    <td colspan="7" class="text-center text-muted py-4">Cargando contratos…</td>
                  </tr>
                  <tr v-else-if="!filteredContracts.length">
                    <td colspan="7" class="text-center text-muted py-4">No hay contratos vigentes para esta empresa.</td>
                  </tr>
                  <tr v-for="c in filteredContracts" :key="c.id" style="cursor:pointer" @click="toggleContract(c.id)">
                    <td><input type="checkbox" :checked="selectedIds.includes(c.id)" @click.stop="toggleContract(c.id)" /></td>
                    <td>{{ c.fullName }}</td>
                    <td>{{ c.documentNumber || '—' }}</td>
                    <td>{{ c.positionName || '—' }}</td>
                    <td>{{ c.area || '—' }}</td>
                    <td class="text-right"><span v-if="c.baseSalary != null">S/ {{ formatNumber(c.baseSalary) }}</span><span v-else>—</span></td>
                    <td><wz-badge :variant="statusVariant(c.displayStatus)">{{ statusLabel(c.displayStatus) }}</wz-badge></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <span v-if="step1Error" class="wz-form-error-msg"><i class="ri-error-warning-line"></i>Seleccione al menos un contrato</span>
          </template>

          <div class="text-right mt-3">
            <wz-button variant="light" @click="$router.push({ name: 'contracts-list' })">Cancelar</wz-button>
            <wz-button variant="primary" class="ml-2" :disabled="selectedIds.length === 0" @click="goStep2">Siguiente</wz-button>
          </div>
        </div>

        <!-- ───────────────── PASO 2 ───────────────── -->
        <div v-if="currentStep === 2">
          <AddendaFieldsForm ref="fieldsForm" :key="fieldsFormKey" v-model="addendaFields" :company-id="companyId" />

          <div class="mt-4" style="max-width: 420px;">
            <wz-datepicker label="Fecha efectiva *" v-model="effectiveDate" :max="maxEffectiveDate" />
            <small v-if="hasNonDateAware" class="text-muted d-block mt-1">
              Los cambios de área, canal, línea de gasto, sede, jefe directo o responsable solo pueden aplicarse a partir de hoy.
            </small>
          </div>

          <div class="mt-3">
            <label>Motivo del cambio <span class="text-danger">*</span> <small class="text-muted">(mín. 10 caracteres)</small></label>
            <textarea v-model="reason" rows="3" maxlength="500" class="wz-form-control mt-2" placeholder="Ej.: Aumento salarial anual aprobado por gerencia"></textarea>
            <small :class="reasonValid ? 'text-muted' : 'text-danger'">{{ (reason || '').trim().length }}/10 mínimos</small>
          </div>

          <div class="mt-3">
            <label>PDF de la adenda <span class="text-danger">*</span> <small class="text-muted">(1 archivo, máx. 100MB)</small></label>
            <wz-file-input :key="fileKey" class="w-full mt-2" id-file="bulk-addendum-upload" :accept-file="['.pdf']" @onChange="onFileChange" />
            <small v-if="file" class="text-muted d-block mt-1">{{ file.name }}</small>
          </div>

          <span v-if="step2Error" class="wz-form-error-msg mt-2 d-block"><i class="ri-error-warning-line"></i>{{ step2Error }}</span>

          <div class="text-right mt-4">
            <wz-button variant="light" @click="currentStep = 1">Anterior</wz-button>
            <wz-button variant="primary" class="ml-2" @click="goStep3">Siguiente</wz-button>
          </div>
        </div>

        <!-- ───────────────── PASO 3 ───────────────── -->
        <div v-if="currentStep === 3">
          <wz-alert variant="info" v-if="!result">
            <strong>Resumen de la adenda masiva</strong>
            <div class="mt-2">Empresa: <strong>{{ companyName }}</strong></div>
            <div>Contratos: <strong>{{ selectedIds.length }}</strong></div>
            <div>Campos a cambiar: <strong>{{ changedFieldsLabels || '—' }}</strong></div>
            <div v-for="line in newValueLines" :key="line">· {{ line }}</div>
            <div>Fecha efectiva: <strong>{{ formatDate(effectiveDate) }}</strong></div>
            <div>Motivo: <strong>{{ reason }}</strong></div>
            <div>PDF: <strong>{{ file ? file.name : '—' }}</strong></div>
          </wz-alert>

          <div v-if="!result" class="mt-3">
            <h6 class="font-bold mb-2">Contratos seleccionados</h6>
            <ul class="selected-list">
              <li v-for="c in selectedContracts" :key="c.id">{{ c.fullName }} <span class="text-muted">({{ c.documentNumber || 's/DNI' }})</span></li>
            </ul>
          </div>

          <!-- Resultado tras aplicar -->
          <div v-if="result" class="mt-2">
            <wz-alert :variant="result.failed > 0 ? 'warning' : 'success'">
              <strong>{{ result.created }}</strong> adenda(s) creada(s).
              <span v-if="result.failed > 0"><strong>{{ result.failed }}</strong> fallida(s).</span>
            </wz-alert>
            <ul v-if="failedResults.length" class="list-unstyled mt-2">
              <li v-for="r in failedResults" :key="r.contractId" class="text-danger text-sm">
                <i class="ri-error-warning-line mr-1"></i>{{ contractName(r.contractId) }}: {{ r.errorMessage }}
              </li>
            </ul>
          </div>

          <div class="text-right mt-4">
            <wz-button v-if="!result" variant="light" @click="currentStep = 2">Volver</wz-button>
            <wz-button v-if="!result" variant="primary" class="ml-2" :disabled="submitting" @click="apply">
              {{ submitting ? 'Aplicando…' : 'Aplicar adenda masiva' }}
            </wz-button>
            <wz-button v-if="result" variant="primary" @click="$router.push({ name: 'contracts-list' })">Ir al listado</wz-button>
          </div>
        </div>

      </section>
    </wz-card>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import CompanyService from '@/core/services/CompanyService'
import ContractsService from '@/core/services/ContractsService'
import ContractAddendumService from '@/core/services/ContractAddendumService'
import AddendaFieldsForm, { NON_DATE_AWARE_FIELDS } from './components/AddendaFieldsForm.vue'

const MAX_SIZE_BYTES = 100 * 1024 * 1024 // 100MB
const FIELD_LABELS = {
  BASE_SALARY: 'Sueldo', POSITION: 'Cargo', AREA: 'Área', CHANNEL: 'Canal',
  EXPENSE_LINE: 'Línea de gasto', WORK_SITE: 'Sede', DIRECT_MANAGER: 'Jefe directo',
  HR_RESPONSIBLE_EMAIL: 'Responsable',
}

export default {
  name: 'ContractAddendaBulk',
  components: { AddendaFieldsForm },
  data() {
    return {
      currentStep: 1,
      companies: [],
      companyId: null,
      contracts: [],
      loadingContracts: false,
      search: '',
      selectedIds: [],
      step1Error: false,
      // Paso 2
      fieldsFormKey: 0,
      addendaFields: { changedFields: [] },
      effectiveDate: null,
      reason: '',
      file: null,
      fileKey: 0,
      step2Error: '',
      // Paso 3
      submitting: false,
      result: null,
    }
  },
  computed: {
    companyOptions() { return this.companies.map(c => ({ key: c.id, label: c.name })) },
    companyName() {
      const c = this.companies.find(x => x.id === this.companyId)
      return c ? c.name : '—'
    },
    filteredContracts() {
      if (!this.search) return this.contracts
      const q = this.search.toLowerCase()
      return this.contracts.filter(c =>
        (c.fullName || '').toLowerCase().includes(q) ||
        (c.documentNumber || '').toLowerCase().includes(q))
    },
    allVisibleSelected() {
      return this.filteredContracts.length > 0 && this.filteredContracts.every(c => this.selectedIds.includes(c.id))
    },
    selectedContracts() {
      return this.contracts.filter(c => this.selectedIds.includes(c.id))
    },
    changedFields() { return this.addendaFields.changedFields || [] },
    changedFieldsLabels() {
      return this.changedFields.map(f => FIELD_LABELS[f] || f).join(', ')
    },
    hasNonDateAware() {
      return this.changedFields.some(f => NON_DATE_AWARE_FIELDS.includes(f))
    },
    maxEffectiveDate() {
      return this.hasNonDateAware ? new Date().toISOString().slice(0, 10) : null
    },
    reasonValid() { return (this.reason || '').trim().length >= 10 },
    newValueLines() {
      const f = this.addendaFields
      const lines = []
      if (f.newBaseSalary != null) lines.push(`Sueldo: S/ ${this.formatNumber(f.newBaseSalary)}`)
      if (f.newPositionId != null) lines.push('Cargo: (nuevo seleccionado)')
      if (f.newAreaId != null) lines.push('Área: (nueva seleccionada)')
      if (f.newChannelId != null) lines.push('Canal: (nuevo seleccionado)')
      if (f.newExpenseLineId != null) lines.push('Línea de gasto: (nueva seleccionada)')
      if (f.newWorkSiteId != null) lines.push('Sede: (nueva seleccionada)')
      if (f.newDirectManagerId != null) lines.push('Jefe directo: (nuevo seleccionado)')
      if (f.newHrResponsibleEmail) lines.push(`Responsable: ${f.newHrResponsibleEmail}`)
      return lines
    },
    failedResults() {
      return this.result && this.result.results ? this.result.results.filter(r => !r.success) : []
    },
  },
  methods: {
    formatNumber(v) {
      if (v == null) return '0.00'
      return Number(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate(value) {
      if (!value) return '—'
      const s = String(value).slice(0, 10).split('-')
      return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : value
    },
    statusLabel(s) {
      const map = { VIGENTE: 'VIGENTE', POR_VENCER: 'POR VENCER' }
      return map[s] || s
    },
    statusVariant(s) {
      return s === 'POR_VENCER' ? 'warning-solid' : 'success-solid'
    },
    async onCompanyChange() {
      this.selectedIds = []
      this.contracts = []
      if (this.companyId) await this.loadContracts()
    },
    async loadContracts() {
      this.loadingContracts = true
      try {
        const data = { status: 'VIGENTE', companyIds: [this.companyId] }
        const params = { page: 0, size: 500, sort: 'id' }
        const resp = await ContractsService.list(data, params)
        this.contracts = (resp.payload && resp.payload.content) || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los contratos')
        this.contracts = []
      } finally {
        this.loadingContracts = false
      }
    },
    toggleContract(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx === -1) this.selectedIds.push(id)
      else this.selectedIds.splice(idx, 1)
    },
    toggleSelectAll() {
      if (this.allVisibleSelected) {
        const visible = this.filteredContracts.map(c => c.id)
        this.selectedIds = this.selectedIds.filter(id => !visible.includes(id))
      } else {
        const ids = new Set(this.selectedIds)
        this.filteredContracts.forEach(c => ids.add(c.id))
        this.selectedIds = Array.from(ids)
      }
    },
    onFileChange(files) {
      this.file = files && files.length > 0 ? files[0] : null
    },
    isPdf(file) {
      return file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf')
    },
    goStep2() {
      this.step1Error = this.selectedIds.length === 0
      if (this.step1Error) return
      this.currentStep = 2
    },
    goStep3() {
      this.step2Error = ''
      if (this.changedFields.length === 0) { this.step2Error = 'Seleccione al menos un campo a modificar'; return }
      const fieldError = this.$refs.fieldsForm && this.$refs.fieldsForm.validate()
      if (fieldError) { this.step2Error = fieldError; return }
      if (!this.effectiveDate) { this.step2Error = 'La fecha efectiva es obligatoria'; return }
      if (this.hasNonDateAware && this.effectiveDate > new Date().toISOString().slice(0, 10)) {
        this.step2Error = 'Los cambios de área/canal/línea/sede/jefe/responsable no admiten fecha futura'; return
      }
      if (!this.reasonValid) { this.step2Error = 'El motivo es obligatorio (mínimo 10 caracteres)'; return }
      if (!this.file) { this.step2Error = 'Adjunte el PDF de la adenda'; return }
      if (!this.isPdf(this.file)) { this.step2Error = 'El archivo debe ser un PDF'; return }
      if (this.file.size > MAX_SIZE_BYTES) { this.step2Error = 'El PDF no debe superar los 100MB'; return }
      this.currentStep = 3
    },
    contractName(contractId) {
      const c = this.contracts.find(x => x.id === contractId)
      return c ? c.fullName : `Contrato #${contractId}`
    },
    async apply() {
      this.submitting = true
      try {
        const f = this.addendaFields
        const resp = await ContractAddendumService.bulk({
          contractIds: this.selectedIds,
          changedFields: f.changedFields,
          newBaseSalary: f.newBaseSalary,
          newPositionId: f.newPositionId,
          newAreaId: f.newAreaId,
          newChannelId: f.newChannelId,
          newExpenseLineId: f.newExpenseLineId,
          newWorkSiteId: f.newWorkSiteId,
          newDirectManagerId: f.newDirectManagerId,
          newHrResponsibleEmail: f.newHrResponsibleEmail,
          effectiveDate: this.effectiveDate,
          reason: this.reason.trim(),
          description: this.reason.trim(),
          file: this.file,
        })
        this.result = resp.payload
        if (this.result.failed === 0) Alert.success(`${this.result.created} adenda(s) creada(s) correctamente`)
        else Alert.alert('Resultado parcial', `${this.result.created} creada(s), ${this.result.failed} fallida(s).`)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al aplicar la adenda masiva')
      } finally {
        this.submitting = false
      }
    },
  },
  async created() {
    try {
      const resp = await CompanyService.listActive()
      this.companies = resp.payload || []
    } catch (e) {
      Alert.error('Error al cargar las empresas')
    }
  },
}
</script>

<style lang="stylus" scoped>
.selected-list
  max-height 220px
  overflow-y auto
  padding-left 18px
  margin 0

.wz-table th, .wz-table td
  padding 8px 10px
</style>
